import { getConfig } from "../../config.js";
import apiClient from "@/utils/httpClient";

const orgHeaders = (): Record<string, string> => {
  const organizationId = apiClient.getOrganizationId();
  const viewId = apiClient.getViewId();
  return {
    ...(organizationId && { 'x-organization-id': organizationId }),
    ...(viewId && { 'x-view-id': viewId }),
  };
};

export interface CompletedStep {
  entity: string;
  created: number;
  updated: number;
  error?: string;
}

export type SyncType = 'INITIAL' | 'FULL';

export interface SyncStatusResponse {
  available: boolean;
  isSyncing: boolean;
  currentSync: {
    id: string;
    state: string;
    syncType: SyncType;
    sinceDate?: string | null;
    currentStep: string;
    completedSteps: CompletedStep[];
    startedAt: string;
  } | null;
  lastSync: {
    id: string;
    state: string;
    syncType: SyncType;
    sinceDate?: string | null;
    currentStep: string;
    completedSteps: CompletedStep[];
    startedAt: string;
    completedAt: string | null;
    errorMessage: string | null;
    syncResult: unknown;
  } | null;
}

/**
 * Fetches sync status using a raw fetch that won't redirect on 401.
 * This is intentional: during the auth callback flow, the token isn't set yet
 * so polling will get 401s until the callback completes. We return null instead
 * of triggering the global auth-expired redirect.
 */
export const getSyncStatus = async (): Promise<SyncStatusResponse | null> => {
  const response = await fetch(`${getConfig().get('api.baseUrl')}/sync/status`, {
    credentials: 'include',
    headers: orgHeaders(),
  });
  if (!response.ok) return null;

  const json = await response.json();
  // Unwrap standard { success, data } envelope
  if (json && json.success && json.data) return json.data;
  return json;
};

const postSync = async (path: string): Promise<unknown> => {
  const response = await fetch(`${getConfig().get('api.baseUrl')}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...orgHeaders() },
  });
  if (!response.ok) {
    throw new Error(`Sync request failed: ${response.statusText}`);
  }
  return response.json();
};

/** Runs the initial in-process sync (limited records) immediately after QB auth. Returns the sync status ID. */
export const startInitialSync = async (): Promise<string> => {
  const json = await postSync('/sync');
  return json.syncStatusId as string;
};

/** Queues a full background sync via Service Bus. Called once on dashboard after initial sync. */
export const startFullSync = (): Promise<void> => postSync('/sync/full');